<template>
  <v-container :fluid="true">
    <ListContainer section-type="photo-panel-inner-top-section" :rtc-id="props.rtcId" :model-value="images"></ListContainer>

    <v-row v-if="!loading">
      <v-col style="position: relative" cols="12" v-for="image in images" :key="image.id">
        <ListContainer section-type="photo-panel-inner-each-image" :rtc-id="props.rtcId" :model-value="image"></ListContainer>

        <v-icon icon="mdi-delete" class="delete-btn" @click="deleteImage(image)"></v-icon>
        <div
          @mouseenter="(e) => onHoverImage(e, image)"
          @mousemove="(e) => onHoverImage(e, image)"
          @mouseleave="(e) => onHoverImage(e, null)"
          style="cursor: zoom-in">
          <img draggable="false" class="image-display" :src="applyApiUrl(image.relativeURI)" />
          <p class="image-title">{{ image.documentType.name }} - {{ image.documentTypeFace.name }}</p>
        </div>
        <v-icon icon="mdi-rotate-right" class="rotate-btn" @click="rotateImage()"></v-icon>
      </v-col>
      <v-col cols="12" style="justify-content: center; align-items: center; display: flex">
        <v-icon icon="mdi-refresh" size="64" @click="actionEmitter.emit(Actions.PhotoPanelUpdate)"></v-icon>
      </v-col>
    </v-row>
    <div v-else style="width: 100%; height: 100%; display: flex; align-content: center; justify-content: center">
      <v-progress-circular indeterminate color="purple" size="64" width="10"></v-progress-circular>
    </div>

    <!-- hover content -->
    <div
      ref="hoverImageRef"
      v-if="hoveredImage"
      class="hover-content"
      :style="
        'right:300px' + '; top:' + hoverMouseTopPx + 'px;' + `transform: rotate(${rotatedImages[hoveredImage?.id] || 0}deg);`
      ">
      <img draggable="false" :src="applyApiUrl(hoveredImage.relativeURI)" />

      <!-- div class="image-title bigger">{{ hoveredImage.documentType.name }} - {{ hoveredImage.documentTypeFace.name }}</!-->
    </div>
  </v-container>
</template>

<script setup>
import { useVideoCallState } from "@/stores/videoCallState";
import { Actions, Events, ChangeSectionPayload, EventManager } from "@events";
import { SectionList } from "@module-system";
import axios from "axios";
import { ref } from "vue";
import Swal from "sweetalert2";
import i18n from "@/i18n";
import ListContainer from "@module-system/ViewModule/ListContainer.vue";
import configurationProvider from "@/Core/configurationProvider";
import { applyApiUrl } from "@/utils";

const hoveredImage = ref(null);
const hoverMouseRightPx = ref(0);
const hoverMouseTopPx = ref(0);
const rotatedImages = ref({});

const isAgentAutoOpenPhotoPanelWhenPhotoTaken_Enabled = configurationProvider.getBool(
  "AGENT_AUTO_OPEN_PHOTO_PANEL_WHEN_PHOTO_TAKEN",
  true,
);

const hoverImageRef = ref(null);
function onHoverImage(event, image) {
  hoveredImage.value = image;
  hoverMouseRightPx.value = 300;
  hoverMouseTopPx.value = event ? event.target.getBoundingClientRect().top : 0;

  // if top + image height > window height, set top to window height - image height - 20px
  if (hoverImageRef.value) {
    const hoverImageHeight = hoverImageRef.value.getBoundingClientRect().height;
    if (hoverMouseTopPx.value + hoverImageHeight > window.innerHeight) {
      hoverMouseTopPx.value = window.innerHeight - hoverImageHeight - 20;
    }
  }

  // if top is less than 0, set top to 20px
  if (hoverMouseTopPx.value < 0) {
    hoverMouseTopPx.value = 20;
  }
}

function rotateImage() {
  if (!hoveredImage.value) {
    return;
  }

  if (rotatedImages.value[hoveredImage.value.id]) {
    if (rotatedImages.value[hoveredImage.value.id] >= 270) {
      rotatedImages.value[hoveredImage.value.id] = 0;
    } else {
      rotatedImages.value[hoveredImage.value.id] += 90;
    }
  } else {
    rotatedImages.value[hoveredImage.value.id] = 90;
  }
}

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
});

const state = useVideoCallState(props.rtcId);

const images = ref([]);
const loading = ref(false);
const actionEmitter = EventManager.getActionEmitter(props.rtcId);
const eventEmitter = EventManager.getEventEmitter(props.rtcId);

async function loadImages() {
  loading.value = true;
  try {
    var res = await axios.get("/api/call/photo/list/" + props.rtcId);
    var resp = res.data;
    if (resp.success == false) {
      console.error("Get Photo List error:", resp.error);
      return;
    }

    for (const dt of resp.data) {
      dt.hover = false;
    }

    images.value = resp.data;
    // set state.callData.documents
    state.callData.documents = resp.data;
  } catch (err) {
    console.log(err);
  } finally {
    loading.value = false;
  }
}

async function deleteImage(image) {
  const result = await Swal.fire({
    title: i18n.global.t("AreYouSureToDelete"),
    text: i18n.global.t("YouWontBeAbleToRevertThis"),
    icon: "warning",
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    showCancelButton: true,
    confirmButtonText: i18n.global.t("Yes"),
    cancelButtonText: i18n.global.t("Cancel"),
    background: "#363535",
    color: "#ffffff",
  });

  console.log({ result });
  if (!result.isConfirmed) {
    return;
  }

  axios
    .delete("/api/call/photo/delete/" + image.id)
    .then((res) => {
      var resp = res.data;
      if (resp.success == false) {
        console.error("Delete Photo error:", resp.error);
        return;
      }
      eventEmitter.emit(Events.PhotoDeleted, {
        rtcId: props.rtcId,
        documentTypeId: image.documentType.id,
        documentTypeFaceId: image.documentTypeFace.id,
      });
      loadImages();
      onHoverImage(null, null);
    })
    .catch((err) => {
      console.log(err);
      onHoverImage(null, null);
    });
}

actionEmitter.on(Actions.PhotoPanelUpdate, () => {
  if (isAgentAutoOpenPhotoPanelWhenPhotoTaken_Enabled) {
    actionEmitter.emit(
      Actions.ChangeSectionModule,
      new ChangeSectionPayload({
        section: SectionList.CallRightPanel,
        key: "photo-panel",
      }),
    );
  }

  loadImages();
});

eventEmitter.once(Events.CallDataReceived, () => {
  loadImages();
});
</script>

<style>
.image-display {
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-height: 200px;
}

.delete-btn {
  position: absolute;
  bottom: 3px;
  right: 3px;
  color: rgb(255, 107, 107);
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  padding: 1.2rem;
  margin: 0.5rem;
  cursor: pointer;
}

.rotate-btn {
  position: absolute;
  bottom: 3px;
  right: 43px;
  color: rgb(107, 255, 112);
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  padding: 1.2rem;
  margin: 0.5rem;
  cursor: pointer;
}

.image-title {
  position: absolute;
  top: 0;
  left: 0;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.5rem;
  margin: 0.5rem;
  font-size: 0.8rem;
}

.image-title.bigger {
  position: absolute;
  bottom: 0;
  left: 0;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.5rem;
  margin: 0.5rem;
  font-size: 1rem;
}

.hover-content {
  position: fixed;
  z-index: 2500;
  display: flex;
  justify-content: end;
  pointer-events: none;
  align-items: flex-end;
}

.hover-content img {
  background-color: black;
  object-fit: contain;
  pointer-events: auto;
  border: 2px solid #737373;
  border-radius: 6px;
  max-height: 45vh;
  max-width: 45vw;
  z-index: 2500;
}
</style>
